
import Vue from 'vue';
import _trim from 'lodash/trim';
import { functions } from '@/firebase';
import { mapGetters, mapActions } from 'vuex';
import { SNACKBAR_STATUS } from '@/constants/constants';
import { CHECKOUT_TEAM_PLAN_SUBSCRIPTION, Team } from '@run-diffusion/shared';
import { RunnitsOwnerSelection } from '@/store';
import { RUNNITS_OWNER_SELECTION } from '@/views/Runnits/constants';
import { titleCase } from '@/filters';
import { noteTeamsAdded } from '@/utils/teamsMirrorLag';
import {
	DEFAULT_TEXT_MAX_LENGTH,
	getShowTextCounterAt,
	getTextMaxLengthRule,
} from '@/utils';
import DialogOrBottomSheet from '@/components/base/DialogOrBottomSheet.vue';
import DialogContent from '@/components/base/DialogContent.vue';
import ActionsIsland from '@/components/ActionsIsland.vue';
import { TeamUserMixin } from '@/mixins';

export default Vue.extend({
	name: 'NewTeam',
	mixins: [
		TeamUserMixin,
	],
	props: {
		value: { type: Boolean, default: false },
		currTeam: { type: Object, default: null },
	},
	data () {
		return {
			// FORM
			formValid: true,
			DEFAULT_TEXT_MAX_LENGTH,
			SHORT_TEXT_LENGTH: 100,
			rules: {
				name: [
					getTextMaxLengthRule(100),
					v => !!_trim(v) || 'Required',
				],
			},
			teamName: null,
			survey: {
				companyName: {
					label: 'What is your company name, if applicable?',
					value: null,
					type: 'TEXT',
				},
				whatWorkingOn: {
					label: 'How do you plan on integrating AI image generation into your projects?',
					value: null,
					type: 'TEXT',
				},
				whoMainUsers: {
					label: 'Who will be the main users within your organization?',
					value: null,
					type: 'TEXT',
				},
				supportNeeded: {
					label: 'What sort of training or support do you anticipate needing?',
					value: null,
					type: 'TEXT',
				},
			},
			saving: false,

			windowView: 0,
			SETUP_TEAM_WINDOW_ITEM: 0,
			GO_TO_PAYMENT_TAB_WINDOW_ITEM: 1,
		};
	},
	computed: {
		...mapGetters([
			'displayLabels',
		]),
		showTeamSetupSurvey () {
			return !this.currTeam;
		},
		dialogTitle () {
			if (this.windowView === this.GO_TO_PAYMENT_TAB_WINDOW_ITEM) {
				return 'Setup Payment';
			}
			return `${titleCase(this.displayLabels.team)} Sign Up`;
		},
	},
	watch: {
		currTeam: {
			immediate: true,
			handler (newVal: Team, oldVal: Team) {
				const newTeamId: string = newVal?.id || null;
				const oldTeamId: string = oldVal?.id || null;
				if (newTeamId !== oldTeamId && newVal) {
					this.initTeamName(newVal);
				}
			},
		},
	},
	methods: {
		...mapActions([
			'updateSnackbar',
			'bindAllTeamData',
		]),
		getShowTextCounterAt,
		setOpen (val: boolean) {
			this.$emit('input', val);
		},
		onClose () {
			this.setOpen(false);
		},
		openStripePaymentTab () {
			const routeData = this.$router.resolve({
				name: 'GoToPayment',
				query: {
					checkoutAction: CHECKOUT_TEAM_PLAN_SUBSCRIPTION,
				},
			});
			window.open(routeData.href, '_blank');
		},
		async onSave () {
			if (!this.$refs.form.validate()) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Form invalid, check fields',
					show: true,
				});
				return;
			}

			const trimmedTeamName: string = _trim(this.teamName);
			const existingTeamOnboarding: boolean = !!(
				this.currTeam &&
				this.currTeam.isOnboarding
			);

			if (!existingTeamOnboarding) {
				const onError: Function = (e) => {
					console.error(e);
					this.updateSnackbar({
						status: SNACKBAR_STATUS.ERROR,
						message: `Error saving ${this.displayLabels.team}, please reach out to report issues by going to Resources -> Help from the menu options`,
						show: true,
					});
				};
				try {
					this.saving = true;
					const functionRef = functions
						.httpsCallable('setupNewTeam');
					const { success, teamId } = (await functionRef({
						teamName: trimmedTeamName,
						mode: 'NEW_TEAM_PLAN',
						...(this.showTeamSetupSurvey && {
							survey: this.survey,
						}),
					})).data;
					if (!success) {
						onError(new Error('setupNewTeam returned success: false'));
					} else {
						// A new Team: the company's Teams and the dashboard cards catch up once the mirror has it
						noteTeamsAdded();
					}

					// Update Vuex
					this.bindAllTeamData(teamId);

					// After they create new team, set variables to auto-select new team in pickers
					const runnitsOwnerSelection: RunnitsOwnerSelection = RUNNITS_OWNER_SELECTION.TEAM;
					this.switchBetweenTeamAndUser(runnitsOwnerSelection);

					this.windowView = this.GO_TO_PAYMENT_TAB_WINDOW_ITEM;
				} catch (e) {
					onError(e);
				} finally {
					this.saving = false;
				}
			} else {
				// If team exists and is onboarding, just move to payment
				this.windowView = this.GO_TO_PAYMENT_TAB_WINDOW_ITEM;
			}
		},
		initTeamName (team: Team) {
			this.teamName = team?.name || null;
		},
	},
	components: {
		DialogOrBottomSheet,
		DialogContent,
		ActionsIsland,
	},
});
